/**
 * Utilidades de estado y año de eventos.
 *
 * El campo `status` del evento en la base de datos nunca se recalcula a partir
 * de la fecha, por lo que los eventos vencidos siguen figurando como "próximo".
 * Estas funciones derivan el estado efectivo desde `eventDate` en tiempo real y
 * solo respetan el valor guardado cuando el evento fue cancelado.
 */

/**
 * Devuelve el año (número) de un evento a partir de su fecha.
 * `eventDate` puede venir como 'YYYY-MM-DD' o como Date/ISO.
 */
export const getEventYear = (event) => {
  if (!event) return null;
  const raw = event.eventDate || event.date;
  if (!raw) return null;

  if (typeof raw === 'string') {
    const match = raw.match(/^(\d{4})-/);
    if (match) return parseInt(match[1], 10);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
};

/**
 * Estado efectivo del evento: 'cancelled' si fue cancelado, 'completed' si la
 * fecha ya pasó, 'upcoming' en caso contrario.
 */
export const getEffectiveEventStatus = (event) => {
  if (!event) return 'upcoming';
  if (event.status === 'cancelled') return 'cancelled';

  const raw = event.eventDate || event.date;
  if (!raw) return event.status || 'upcoming';

  const eventDate = new Date(
    typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T23:59:59` : raw,
  );
  if (Number.isNaN(eventDate.getTime())) return event.status || 'upcoming';

  return eventDate.getTime() < Date.now() ? 'completed' : 'upcoming';
};

/** Un evento es activo salvo que se lo haya marcado explícitamente como inactivo. */
export const isEventActive = (event) => Boolean(event) && event.isActive !== false;

/** Descarta los eventos inactivos, para no ofrecerlos en los filtros. */
export const filterActiveEvents = (events = []) => events.filter(isEventActive);

/**
 * Eventos a los que todavía se puede asignar un alumno: activos y no realizados.
 * `keepId` conserva el evento ya seleccionado (por ejemplo, al editar).
 */
export const filterSelectableEvents = (events = [], keepId = null) =>
  events.filter(
    (event) =>
      event.id === keepId ||
      (isEventActive(event) && getEffectiveEventStatus(event) === 'upcoming'),
  );

/**
 * Construye la lista de años disponibles (descendente) a partir de los eventos
 * activos, garantizando que el año actual siempre esté presente.
 */
export const buildYearOptions = (events = []) => {
  const years = new Set();
  years.add(new Date().getFullYear());

  filterActiveEvents(events).forEach((event) => {
    const year = getEventYear(event);
    if (year) years.add(year);
  });

  return Array.from(years).sort((a, b) => b - a);
};

/** Año actual como string, para usar como valor por defecto de los filtros. */
export const getCurrentYear = () => String(new Date().getFullYear());
